"use client";
import React, { useRef } from "react";
import { motion, useInView } from "motion/react";
import AnimatedCard from "@/components/ui/animated-card";
import BorderWrapper from "@/components/ui/border-wrapper";
import { useTranslations } from "next-intl";

const shapeVariants = {
  visible: {
    scale: [0.9, 1, 0.9] as number[],
    transition: { duration: 5, ease: "easeInOut", repeat: Infinity },
  },
} as const;

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
} as const;

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
} as const;

const ServicesIntro = () => {
  const t = useTranslations("services");
  const highlights = [
    { value: t("intro.stats.servicesCount"), label: t("intro.stats.servicesLabel") },
    { value: t("intro.stats.projectsCount"), label: t("intro.stats.projectsLabel") },
    { value: t("intro.stats.clientsCount"), label: t("intro.stats.clientsLabel") },
    { value: t("intro.stats.countriesCount"), label: t("intro.stats.countriesLabel") },
  ];
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: "0px 0px -80px 0px" });

  return (
    <section ref={sectionRef} className="section pt-[60px] lg:pt-[140px] pb-[60px] lg:pb-[120px] relative">
      <div
        className="absolute right-[-5%] top-[10%] w-[500px] h-[500px] bg-[linear-gradient(180deg,_rgba(135,82,250,0.1)_0%,_rgba(34,12,81,0)_100%)] rounded-full blur-[120px] pointer-events-none animate-shape-pulse"
        />

      <div className="container relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "show" : "hidden"}
          className="grid md:grid-cols-2 gap-[40px] md:gap-[50px] lg:gap-[80px] items-center"
        >
          {/* Left: text */}
          <div className="space-y-[24px]">
            <motion.h2
              variants={itemVariants}
              className="text-[28px] md:text-[40px] lg:text-[44px] font-semibold leading-[1.15]"
            >
              {t("intro.title")}
            </motion.h2>

            <motion.p
              variants={itemVariants}
              className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90"
            >
              {t("intro.description")}
            </motion.p>

            <motion.p
              variants={itemVariants}
              className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90"
            >
              {t("intro.description2")}
            </motion.p>
          </div>

          {/* Right: stat cards */}
          <motion.div variants={itemVariants}>
            <AnimatedCard
              className="rounded-[20px] lg:rounded-[24px]"
              circleClassName="w-[160px] h-[160px] blur-[90px]"
            >
              <BorderWrapper
                wrapperClass="rounded-[20px] lg:rounded-[24px]"
                className="rounded-[20px] lg:rounded-[24px] bg-gradient-to-b from-[#121124] to-[#0e0821] overflow-hidden relative"
              >
                <div
                  className="absolute top-0 right-0 w-[250px] h-[250px] bg-theme/[0.08] rounded-full blur-[80px] pointer-events-none animate-shape-pulse"
                  />
                <div className="grid grid-cols-2 divide-x divide-y divide-white/[0.06]">
                  {highlights.map((item, i) => (
                    <div
                      key={i}
                      className="px-[28px] lg:px-[36px] py-[28px] lg:py-[36px] relative group"
                    >
                      <div className="text-[32px] md:text-[40px] lg:text-[52px] font-semibold text-[#4a3290] leading-[1] mb-[8px]">
                        {item.value}
                      </div>
                      <div className="text-[13px] font-light text-foreground/90">
                        {item.label}
                      </div>
                    </div>
                  ))}
                </div>
              </BorderWrapper>
            </AnimatedCard>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesIntro;
